<template>
  <div class="crm-dash">
    <div class="page-header">
      <div>
        <h5 class="page-title">CRM Dashboard</h5>
        <p class="page-sub">Overview of leads, contacts, opportunities and customer engagement</p>
      </div>
      <button class="btn-refresh" @click="load" :disabled="loading" title="Refresh">
        <font-awesome-icon icon="sync-alt" :class="{ 'fa-spin': loading }" />
      </button>
    </div>

    <!-- KPI Cards -->
    <div class="kpi-grid">
      <template v-if="loading">
        <div class="kpi-card" v-for="n in 8" :key="n">
          <div class="skeleton" style="width:36px;height:36px;border-radius:10px"></div>
          <div style="flex:1">
            <div class="skeleton" style="height:12px;width:60%;border-radius:4px;margin-bottom:8px"></div>
            <div class="skeleton" style="height:22px;width:45%;border-radius:4px"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="kpi-card" @click="go('CRM Leads')">
          <div class="kpi-icon kpi-icon--blue"><font-awesome-icon icon="user-plus" /></div>
          <div>
            <p class="kpi-label">Total Leads</p>
            <p class="kpi-value">{{ kpis.total_leads ?? 0 }}</p>
            <p class="kpi-sub" v-if="kpis.new_leads_this_month">{{ kpis.new_leads_this_month }} this month</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('CRM Contacts')">
          <div class="kpi-icon kpi-icon--green"><font-awesome-icon icon="address-book" /></div>
          <div>
            <p class="kpi-label">Contacts</p>
            <p class="kpi-value">{{ kpis.total_contacts ?? 0 }}</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('CRM Opportunities')">
          <div class="kpi-icon kpi-icon--indigo"><font-awesome-icon icon="chart-line" /></div>
          <div>
            <p class="kpi-label">Open Opportunities</p>
            <p class="kpi-value">{{ kpis.open_opportunities ?? 0 }}</p>
            <p class="kpi-sub" v-if="kpis.pipeline_value">{{ fmtMoney(kpis.pipeline_value) }} pipeline</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('CRM Accounts')">
          <div class="kpi-icon kpi-icon--teal"><font-awesome-icon icon="building" /></div>
          <div>
            <p class="kpi-label">Accounts</p>
            <p class="kpi-value">{{ kpis.total_accounts ?? 0 }}</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('CRM Cases')">
          <div class="kpi-icon kpi-icon--red"><font-awesome-icon icon="ticket-alt" /></div>
          <div>
            <p class="kpi-label">Open Cases</p>
            <p class="kpi-value">{{ kpis.open_cases ?? 0 }}</p>
            <p v-if="kpis.sla_breached" class="kpi-sub kpi-sub--warn">{{ kpis.sla_breached }} SLA breached</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('CRM Campaigns')">
          <div class="kpi-icon kpi-icon--orange"><font-awesome-icon icon="bullhorn" /></div>
          <div>
            <p class="kpi-label">Active Campaigns</p>
            <p class="kpi-value">{{ kpis.active_campaigns ?? 0 }}</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('CRM Surveys')">
          <div class="kpi-icon kpi-icon--purple"><font-awesome-icon icon="poll" /></div>
          <div>
            <p class="kpi-label">Active Surveys</p>
            <p class="kpi-value">{{ kpis.active_surveys ?? 0 }}</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('CRM Activities')">
          <div class="kpi-icon kpi-icon--pink"><font-awesome-icon icon="calendar-check" /></div>
          <div>
            <p class="kpi-label">Pending Activities</p>
            <p class="kpi-value">{{ kpis.pending_activities ?? 0 }}</p>
          </div>
        </div>
      </template>
    </div>

    <!-- Charts Row 1: Leads by Status + Pipeline by Stage -->
    <div class="charts-row">
      <div class="chart-card">
        <div class="chart-card-header">
          <span class="chart-title">Leads by Status</span>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton"></div>
          <div v-else-if="!leads_by_status.length" class="chart-empty">No lead data</div>
          <GChart v-else type="PieChart" :data="leadsStatusChartData" :options="donutOptions('Leads by Status')" />
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-card-header">
          <span class="chart-title">Opportunity Pipeline</span>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton"></div>
          <div v-else-if="!pipeline_by_stage.length" class="chart-empty">No pipeline data</div>
          <GChart v-else type="ColumnChart" :data="pipelineChartData" :options="columnOptions('Opportunities by Stage')" />
        </div>
      </div>
    </div>

    <!-- Charts Row 2: Cases by Status + Activities by Type -->
    <div class="charts-row">
      <div class="chart-card">
        <div class="chart-card-header">
          <span class="chart-title">Cases by Status</span>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton"></div>
          <div v-else-if="!cases_by_status.length" class="chart-empty">No case data</div>
          <GChart v-else type="PieChart" :data="casesStatusChartData" :options="donutOptions('Cases by Status')" />
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-card-header">
          <span class="chart-title">Activities by Type</span>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton"></div>
          <div v-else-if="!activities_by_type.length" class="chart-empty">No activity data</div>
          <GChart v-else type="BarChart" :data="activitiesTypeChartData" :options="barOptions('Activities by Type')" />
        </div>
      </div>
    </div>

    <!-- Charts Row 3: Leads Trend (full width) + Won vs Lost -->
    <div class="charts-row">
      <div class="chart-card chart-card--wide">
        <div class="chart-card-header">
          <span class="chart-title">Lead Volume — Last 6 Months</span>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton"></div>
          <div v-else-if="!leads_trend.length" class="chart-empty">No trend data</div>
          <GChart v-else type="LineChart" :data="leadsTrendChartData" :options="lineOptions('New Leads')" />
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-card-header">
          <span class="chart-title">Won vs Lost — Last 6 Months</span>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton"></div>
          <div v-else-if="!won_vs_lost.length" class="chart-empty">No data</div>
          <GChart v-else type="ColumnChart" :data="wonLostChartData" :options="wonLostOptions" />
        </div>
      </div>
    </div>

    <!-- Recent Leads -->
    <div v-if="!loading && recent_leads.length" class="section-card">
      <div class="section-header">
        <span class="section-title">Recent Leads</span>
        <button class="btn-link" @click="go('CRM Leads')">View all →</button>
      </div>
      <table class="data-table">
        <thead>
          <tr><th>Name</th><th>Source</th><th>Status</th><th>Created</th></tr>
        </thead>
        <tbody>
          <tr v-for="l in recent_leads" :key="l.id">
            <td>{{ l.full_name }}</td>
            <td>{{ l.source ?? '—' }}</td>
            <td><span :class="['status-badge', `status-badge--${l.status}`]">{{ l.status }}</span></td>
            <td>{{ fmtDate(l.created_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Upcoming Activities -->
    <div v-if="!loading && recent_activities.length" class="section-card">
      <div class="section-header">
        <span class="section-title">Upcoming Activities</span>
        <button class="btn-link" @click="go('CRM Activities')">View all →</button>
      </div>
      <table class="data-table">
        <thead>
          <tr><th>Type</th><th>Subject</th><th>Due</th><th>Status</th></tr>
        </thead>
        <tbody>
          <tr v-for="a in recent_activities" :key="a.id">
            <td>{{ a.type }}</td>
            <td>{{ a.title }}</td>
            <td>{{ a.due_at ? fmtDate(a.due_at) : '—' }}</td>
            <td><span :class="['status-badge', `status-badge--${a.status}`]">{{ a.status }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { GChart } from 'vue-google-charts'

const router  = useRouter()
const loading = ref(false)
const raw     = ref({})

const kpis               = computed(() => raw.value.kpis               ?? {})
const recent_leads       = computed(() => raw.value.recent_leads       ?? [])
const recent_activities  = computed(() => raw.value.recent_activities  ?? [])
const pipeline_by_stage  = computed(() => raw.value.pipeline_by_stage  ?? [])
const leads_by_status    = computed(() => raw.value.leads_by_status    ?? [])
const cases_by_status    = computed(() => raw.value.cases_by_status    ?? [])
const activities_by_type = computed(() => raw.value.activities_by_type ?? [])
const leads_trend        = computed(() => raw.value.leads_trend        ?? [])
const won_vs_lost        = computed(() => raw.value.won_vs_lost        ?? [])

const load = async () => {
  loading.value = true
  try {
    const res  = await axios.get('/crm/dashboard')
    raw.value  = res.data.data ?? res.data
  } catch {
    raw.value = {}
  } finally {
    loading.value = false
  }
}

// ── Chart data transforms ──────────────────────────────────────────────────

const leadsStatusChartData = computed(() => [
  ['Status', 'Count'],
  ...leads_by_status.value.map(r => [capitalize(r.status), Number(r.count)]),
])

const pipelineChartData = computed(() => [
  ['Stage', 'Deals', { role: 'style' }],
  ...pipeline_by_stage.value.map((r, i) => [
    capitalize(r.stage.replace(/_/g, ' ')),
    Number(r.count),
    stageColor(i),
  ]),
])

const casesStatusChartData = computed(() => [
  ['Status', 'Count'],
  ...cases_by_status.value.map(r => [capitalize(r.status.replace(/_/g, ' ')), Number(r.count)]),
])

const activitiesTypeChartData = computed(() => [
  ['Type', 'Count', { role: 'style' }],
  ...activities_by_type.value.map((r, i) => [
    capitalize(r.type),
    Number(r.count),
    typeColor(i),
  ]),
])

const leadsTrendChartData = computed(() => [
  ['Month', 'New Leads'],
  ...leads_trend.value.map(r => [r.month, Number(r.count)]),
])

const wonLostChartData = computed(() => [
  ['Month', 'Won', 'Lost'],
  ...won_vs_lost.value.map(r => [r.month, Number(r.won), Number(r.lost)]),
])

// ── Chart option factories ─────────────────────────────────────────────────

const BLUE   = '#004AAD'
const GREEN  = '#16a34a'
const RED    = '#dc2626'
const FONT   = 'Inter, sans-serif'
const COLORS = ['#004AAD','#0ea5e9','#8b5cf6','#f59e0b','#10b981','#ef4444','#ec4899','#6366f1']

const donutOptions = (title) => ({
  title,
  titleTextStyle: { color: '#1a2b45', fontSize: 13, bold: true, fontName: FONT },
  pieHole: 0.42,
  height: 280,
  chartArea: { width: '90%', height: '80%' },
  legend: { position: 'right', textStyle: { color: '#5a6a85', fontSize: 12, fontName: FONT } },
  colors: COLORS,
  tooltip: { textStyle: { fontName: FONT, fontSize: 12 } },
})

const columnOptions = (title) => ({
  title,
  titleTextStyle: { color: '#1a2b45', fontSize: 13, bold: true, fontName: FONT },
  height: 280,
  chartArea: { width: '80%', height: '68%' },
  legend: { position: 'none' },
  hAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: 'transparent' } },
  vAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: '#f0f4fa' }, minValue: 0 },
  bar: { groupWidth: '55%' },
  tooltip: { textStyle: { fontName: FONT, fontSize: 12 } },
})

const barOptions = (title) => ({
  title,
  titleTextStyle: { color: '#1a2b45', fontSize: 13, bold: true, fontName: FONT },
  height: 280,
  chartArea: { width: '65%', height: '80%' },
  legend: { position: 'none' },
  hAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: '#f0f4fa' }, minValue: 0 },
  vAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: 'transparent' } },
  tooltip: { textStyle: { fontName: FONT, fontSize: 12 } },
})

const lineOptions = (title) => ({
  title,
  titleTextStyle: { color: '#1a2b45', fontSize: 13, bold: true, fontName: FONT },
  height: 280,
  chartArea: { width: '85%', height: '70%' },
  legend: { position: 'none' },
  colors: [BLUE],
  curveType: 'function',
  pointSize: 5,
  pointShape: 'circle',
  hAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: 'transparent' } },
  vAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: '#f0f4fa' }, minValue: 0 },
  tooltip: { textStyle: { fontName: FONT, fontSize: 12 } },
})

const wonLostOptions = {
  title: 'Won vs Lost',
  titleTextStyle: { color: '#1a2b45', fontSize: 13, bold: true, fontName: FONT },
  height: 280,
  chartArea: { width: '80%', height: '68%' },
  colors: [GREEN, RED],
  legend: { position: 'top', textStyle: { color: '#5a6a85', fontSize: 12, fontName: FONT } },
  hAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: 'transparent' } },
  vAxis: { textStyle: { color: '#5a6a85', fontSize: 11, fontName: FONT }, gridlines: { color: '#f0f4fa' }, minValue: 0 },
  bar: { groupWidth: '60%' },
  isStacked: false,
  tooltip: { textStyle: { fontName: FONT, fontSize: 12 } },
}

// ── Helpers ────────────────────────────────────────────────────────────────
const stageColor = (i) => COLORS[i % COLORS.length]
const typeColor  = (i) => COLORS[i % COLORS.length]
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s

const go       = name => router.push({ name })
const fmtMoney = v => Number(v).toLocaleString('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 })
const fmtDate  = v => new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

onMounted(load)
</script>

<style scoped>
.crm-dash { padding-bottom: 40px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:24px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-refresh { width:34px; height:34px; border-radius:8px; border:1px solid #dde5f0; background:#fff; cursor:pointer; color:#7a90b0; font-size:14px; display:flex; align-items:center; justify-content:center; }
.btn-refresh:hover { background:#f4f7fb; color:#004AAD; }

/* KPI grid */
.kpi-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(200px, 1fr)); gap:14px; margin-bottom:28px; }
.kpi-card { background:#fff; border:1.5px solid #e8edf5; border-radius:12px; padding:18px 16px; display:flex; align-items:center; gap:14px; cursor:pointer; transition:box-shadow .15s, border-color .15s; }
.kpi-card:hover { border-color:#004AAD; box-shadow:0 2px 12px rgba(0,74,173,.08); }
.kpi-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.kpi-icon--blue   { background:#e8f0fb; color:#004AAD; }
.kpi-icon--green  { background:#dcfce7; color:#166534; }
.kpi-icon--indigo { background:#ede9fe; color:#6d28d9; }
.kpi-icon--teal   { background:#ccfbf1; color:#0f766e; }
.kpi-icon--red    { background:#fee2e2; color:#991b1b; }
.kpi-icon--orange { background:#ffedd5; color:#9a3412; }
.kpi-icon--purple { background:#f3e8ff; color:#7c3aed; }
.kpi-icon--pink   { background:#fce7f3; color:#9d174d; }
.kpi-label { font-size:11.5px; font-weight:600; color:#7a90b0; margin:0 0 3px; text-transform:uppercase; letter-spacing:.04em; }
.kpi-value { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 2px; }
.kpi-sub   { font-size:11px; color:#7a90b0; margin:0; }
.kpi-sub--warn { color:#e03535; }

/* Charts */
.charts-row { display:grid; grid-template-columns:1fr 1fr; gap:18px; margin-bottom:18px; }
@media (max-width: 768px) { .charts-row { grid-template-columns:1fr; } }
.chart-card { background:#fff; border:1.5px solid #e8edf5; border-radius:12px; overflow:hidden; }
.chart-card--wide { grid-column: 1 / 2; }
.chart-card-header { padding:14px 18px 0; }
.chart-title { font-size:13px; font-weight:700; color:#1a2b45; }
.chart-body { padding:4px 8px 14px; }
.chart-skeleton { height:280px; background:#e8edf5; animation:pulse 1.4s ease-in-out infinite; border-radius:8px; margin:8px; }
.chart-empty { height:280px; display:flex; align-items:center; justify-content:center; color:#b0bec8; font-size:13px; }

/* Tables */
.section-card { background:#fff; border:1.5px solid #e8edf5; border-radius:12px; overflow:hidden; margin-bottom:20px; }
.section-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f4fa; }
.section-title { font-size:14px; font-weight:700; color:#1a2b45; }
.btn-link { background:none; border:none; color:#004AAD; font-size:13px; cursor:pointer; padding:0; }
.btn-link:hover { text-decoration:underline; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table th { background:#f4f7fb; font-weight:600; color:#5a6a85; padding:11px 14px; text-align:left; white-space:nowrap; }
.data-table td { padding:11px 14px; border-top:1px solid #f0f4fa; color:#1a2b45; vertical-align:middle; }
.data-table tbody tr:hover { background:#f9fbff; }

/* Status badges */
.status-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 9px; text-transform:capitalize; background:#f1f5ff; color:#004AAD; }
.status-badge--new        { background:#e0f2fe; color:#075985; }
.status-badge--contacted  { background:#ede9fe; color:#6d28d9; }
.status-badge--qualified  { background:#dcfce7; color:#166534; }
.status-badge--converted  { background:#ccfbf1; color:#0f766e; }
.status-badge--lost       { background:#fee2e2; color:#991b1b; }
.status-badge--open       { background:#e0f2fe; color:#075985; }
.status-badge--pending    { background:#fef3c7; color:#92400e; }
.status-badge--completed  { background:#dcfce7; color:#166534; }
.status-badge--cancelled  { background:#f3f4f6; color:#6b7280; }
.status-badge--active     { background:#dcfce7; color:#166534; }

.skeleton { background:#e8edf5; animation:pulse 1.4s ease-in-out infinite; }
@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.5; } }
</style>
